// ─────────────────────────────────────────────────────────────
// Lightweight module views embedded inside the Founder workspace.
// Each view shows relevant data the founder has permission to see.
// Full detail pages link through to the main ERP modules.
// ─────────────────────────────────────────────────────────────
import { ExternalLink, ChevronLeft, ChevronRight, CheckSquare, Flag, AlertTriangle } from 'lucide-react'
import { getClients } from '../../services/clients.service'
import { getDeals } from '../../services/deals.service'
import { getInvoices } from '../../services/invoices.service'
import { getExpenses } from '../../services/expenses.service'
import { getCampaigns } from '../../services/campaigns.service'
import { getProjects } from '../../services/projects.service'
import { getCalendarEvents } from '../../services/calendar.service'
import { useAuth } from '../../context/AuthContext'
import { useRealtime } from '../../context/RealtimeContext'
import { getEmployeesForFounder } from '../../services/auth.service'
import { Permission } from '../../types/auth.types'
import { useState, useEffect } from 'react'
import type { Client, Deal, Invoice, Expense, Campaign, Project, CalendarEvent } from '../../types'

function SectionHeader({ title, sub, link }: { title: string; sub: string; link?: string }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <div>
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{title}</h1>
        <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>{sub}</p>
      </div>
      {link && (
        <a href={link} target="_blank" rel="noreferrer"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium"
          style={{ border: '1px solid rgba(255,198,0,0.25)', color: '#ffc600' }}>
          <ExternalLink size={13} /> Full view
        </a>
      )}
    </div>
  )
}

function formatINR(n: number) {
  return '₹' + n.toLocaleString('en-IN')
}

function LoadingState() {
  return <p className="text-sm p-6" style={{ color: '#555' }}>Loading…</p>
}

// ── Clients ───────────────────────────────────────────────────
export function ClientsView() {
  const { hasPermission } = useAuth()
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getClients().then(setClients).catch(console.error).finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingState />

  return (
    <div className="p-6">
      <SectionHeader title="Clients" sub={`${clients.filter(c => c.status === 'active').length} active retainers`} link="/clients" />
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,198,0,0.12)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.1)' }}>
              {['Client', 'Services', 'MRR', 'Status'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#a0a0a0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clients.slice(0, 8).map(c => (
              <tr key={c.id} style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.06)' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#1a1a1a')}
                onMouseLeave={e => (e.currentTarget.style.background = '#0f0f0f')}>
                <td className="px-4 py-3 font-medium text-white">{c.name}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap">
                    {c.services.slice(0, 2).map(s => (
                      <span key={s} className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,198,0,0.1)', color: '#ffc600' }}>{s}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 font-semibold" style={{ color: '#ffc600' }}>{formatINR(c.mrr)}</td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-0.5 rounded capitalize"
                    style={c.status === 'active' ? { background: 'rgba(232,160,0,0.12)', color: '#e8a000' } : { background: 'rgba(85,85,85,0.2)', color: '#555' }}>
                    {c.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Pipeline ──────────────────────────────────────────────────
export function PipelineView() {
  const [deals,   setDeals]   = useState<Deal[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getDeals().then(setDeals).catch(console.error).finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingState />

  const activeDeals = deals.filter(d => d.stage !== 'closed-lost')

  return (
    <div className="p-6">
      <SectionHeader title="Sales Pipeline" sub={`${activeDeals.length} active deals`} link="/pipeline" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {activeDeals.slice(0, 8).map(deal => (
          <div key={deal.id} className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
            <div className="flex items-start justify-between mb-1">
              <p className="text-sm font-semibold text-white">{deal.company}</p>
              <span className="text-xs px-2 py-0.5 rounded capitalize" style={{ background: 'rgba(255,198,0,0.1)', color: '#ffc600' }}>{deal.stage}</span>
            </div>
            <p className="text-xs mb-2" style={{ color: '#a0a0a0' }}>{deal.title}</p>
            <p className="text-base font-bold" style={{ color: '#ffc600', fontFamily: 'Syne, sans-serif' }}>{formatINR(deal.value)}</p>
            <p className="text-xs mt-1" style={{ color: '#555' }}>Close: {new Date(deal.expectedClose).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} · {deal.probability}%</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Invoices ──────────────────────────────────────────────────
export function InvoicesView() {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    getInvoices().then(setInvoices).catch(console.error).finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingState />

  return (
    <div className="p-6">
      <SectionHeader title="Invoices" sub={`${invoices.filter(i => i.status === 'overdue').length} overdue`} link="/invoices" />
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,198,0,0.12)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.1)' }}>
              {['Invoice', 'Client', 'Total', 'Status', 'Due'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#a0a0a0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.slice(0, 8).map(inv => {
              const sColor = inv.status === 'paid' ? '#e8a000' : inv.status === 'overdue' ? '#cc3300' : inv.status === 'sent' ? '#ffc600' : '#555'
              return (
                <tr key={inv.id} style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.06)', borderLeft: inv.status === 'overdue' ? '2px solid #cc3300' : '2px solid transparent' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#1a1a1a')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#0f0f0f')}>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: '#ffc600' }}>{inv.invoiceNumber}</td>
                  <td className="px-4 py-3 text-white">{inv.clientName}</td>
                  <td className="px-4 py-3 font-semibold text-white">{formatINR(inv.total)}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded capitalize" style={{ background: `${sColor}18`, color: sColor }}>{inv.status}</span></td>
                  <td className="px-4 py-3 text-xs" style={{ color: inv.status === 'overdue' ? '#cc3300' : '#555' }}>
                    {new Date(inv.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Expenses ──────────────────────────────────────────────────
export function ExpensesView() {
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    getExpenses().then(setExpenses).catch(console.error).finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingState />

  const total = expenses.reduce((s, e) => s + e.amount, 0)

  return (
    <div className="p-6">
      <SectionHeader title="Expenses" sub={`This month: ${formatINR(total)}`} link="/expenses" />
      <div className="space-y-2">
        {expenses.slice(0, 10).map(exp => (
          <div key={exp.id} className="flex items-center justify-between px-4 py-3 rounded-xl"
            style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.08)' }}>
            <div>
              <p className="text-sm font-medium text-white">{exp.description}</p>
              <p className="text-xs" style={{ color: '#555' }}>{exp.vendor} · {exp.category}</p>
            </div>
            <p className="text-sm font-semibold" style={{ color: '#ffc600' }}>{formatINR(exp.amount)}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Campaigns ─────────────────────────────────────────────────
export function CampaignsView() {
  const { hasPermission } = useAuth()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading,   setLoading]   = useState(true)

  useEffect(() => {
    getCampaigns().then(setCampaigns).catch(console.error).finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingState />

  const activeCampaigns = campaigns.filter(c => c.status === 'active')

  return (
    <div className="p-6">
      <SectionHeader title="Campaigns" sub={`${activeCampaigns.length} active`} link="/campaigns" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {activeCampaigns.map(c => {
          const pct = c.budget > 0 ? Math.min((c.spend / c.budget) * 100, 100) : 0
          const roasColor = c.roas >= 3 ? '#e8a000' : c.roas >= 1.5 ? '#ff8c00' : '#cc3300'
          return (
            <div key={c.id} className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-semibold text-white">{c.name}</p>
                  <p className="text-xs" style={{ color: '#a0a0a0' }}>{c.clientName}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(255,198,0,0.1)', color: '#ffc600' }}>{c.type}</span>
              </div>
              <div className="mb-3">
                <div className="flex justify-between text-xs mb-1" style={{ color: '#555' }}>
                  <span>Spend: {formatINR(c.spend)}</span>
                  <span>Budget: {formatINR(c.budget)}</span>
                </div>
                <div className="h-1.5 rounded-full" style={{ background: '#1a1a1a' }}>
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: '#ffc600' }} />
                </div>
              </div>
              <div className="flex justify-between text-xs">
                <span style={{ color: '#555' }}>{c.clicks.toLocaleString('en-IN')} clicks</span>
                <span style={{ color: '#555' }}>{c.conversions} conv.</span>
                <span className="font-semibold" style={{ color: roasColor }}>ROAS {c.roas.toFixed(2)}x</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Ad Performance ────────────────────────────────────────────
export function AdPerformanceView() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading,   setLoading]   = useState(true)

  useEffect(() => {
    getCampaigns().then(setCampaigns).catch(console.error).finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingState />

  const total  = campaigns.reduce((s, c) => s + c.revenue, 0)
  const spend  = campaigns.reduce((s, c) => s + c.spend, 0)
  const roas   = spend > 0 ? total / spend : 0

  return (
    <div className="p-6">
      <SectionHeader title="Ad Performance" sub="Across all active campaigns" link="/campaigns" />
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Spend',   value: formatINR(spend),       color: '#ffc600' },
          { label: 'Total Revenue', value: formatINR(total),       color: '#e8a000' },
          { label: 'Overall ROAS',  value: `${roas.toFixed(2)}x`,  color: roas >= 3 ? '#e8a000' : '#ff8c00' },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-xl p-4 text-center" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
            <p className="text-xl font-bold" style={{ fontFamily: 'Syne, sans-serif', color }}>{value}</p>
            <p className="text-xs mt-1" style={{ color: '#555' }}>{label}</p>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {campaigns.filter(c => c.roas > 0).sort((a, b) => b.roas - a.roas).map(c => (
          <div key={c.id} className="flex items-center gap-4 px-4 py-3 rounded-xl"
            style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.08)' }}>
            <p className="text-sm font-medium text-white flex-1 truncate">{c.name}</p>
            <span className="text-xs" style={{ color: '#555' }}>{formatINR(c.spend)} spend</span>
            <span className="text-sm font-bold" style={{ color: c.roas >= 3 ? '#e8a000' : c.roas >= 1.5 ? '#ff8c00' : '#cc3300' }}>
              {c.roas.toFixed(2)}x
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Budget Tracker ────────────────────────────────────────────
export function BudgetView() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading,   setLoading]   = useState(true)

  useEffect(() => {
    getCampaigns().then(setCampaigns).catch(console.error).finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingState />

  return (
    <div className="p-6">
      <SectionHeader title="Budget Tracker" sub="Ad spend vs allocated budget" />
      <div className="space-y-3">
        {campaigns.map(c => {
          const pct  = c.budget > 0 ? Math.min((c.spend / c.budget) * 100, 100) : 0
          const over = pct >= 90
          return (
            <div key={c.id} className="rounded-xl p-4" style={{ background: '#0f0f0f', border: `1px solid ${over ? 'rgba(204,51,0,0.25)' : 'rgba(255,198,0,0.12)'}` }}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-white">{c.name}</p>
                <span className="text-xs font-semibold" style={{ color: over ? '#cc3300' : '#a0a0a0' }}>{pct.toFixed(0)}% used</span>
              </div>
              <div className="h-2 rounded-full mb-2" style={{ background: '#1a1a1a' }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: over ? '#cc3300' : '#ffc600' }} />
              </div>
              <div className="flex justify-between text-xs" style={{ color: '#555' }}>
                <span>Spent: {formatINR(c.spend)}</span>
                <span>Budget: {formatINR(c.budget)}</span>
                <span>Remaining: {formatINR(Math.max(0, c.budget - c.spend))}</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Projects ──────────────────────────────────────────────────
export function ProjectsView() {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    getProjects().then(setProjects).catch(console.error).finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingState />

  const activeProjects = projects.filter(p => p.status !== 'done')

  return (
    <div className="p-6">
      <SectionHeader title="Projects" sub={`${activeProjects.length} active`} link="/projects" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {activeProjects.map(p => {
          const sColor = p.status === 'in-progress' ? '#ffc600' : p.status === 'review' ? '#ff8c00' : '#555'
          return (
            <div key={p.id} className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
              <div className="flex items-start justify-between mb-1">
                <p className="text-sm font-semibold text-white">{p.title}</p>
                <span className="text-xs px-2 py-0.5 rounded capitalize ml-2 flex-shrink-0" style={{ background: `${sColor}18`, color: sColor }}>{p.status}</span>
              </div>
              <p className="text-xs mb-3" style={{ color: '#a0a0a0' }}>{p.clientName}</p>
              <div className="h-1.5 rounded-full mb-1" style={{ background: '#1a1a1a' }}>
                <div className="h-full rounded-full" style={{ width: `${p.progress}%`, background: '#ffc600' }} />
              </div>
              <p className="text-xs" style={{ color: '#555' }}>{p.progress}% complete · Due {new Date(p.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Content Calendar ──────────────────────────────────────────
export function ContentCalendarView() {
  const today = new Date()
  const [year,   setYear]   = useState(today.getFullYear())
  const [month,  setMonth]  = useState(today.getMonth())
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getCalendarEvents().then(setEvents).catch(console.error).finally(() => setLoading(false))
  }, [])

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']
  const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']

  const firstDay  = new Date(year, month, 1).getDay()
  const daysInMon = new Date(year, month + 1, 0).getDate()

  const cells: (number | null)[] = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMon }, (_, i) => i + 1)]
  while (cells.length % 7 !== 0) cells.push(null)

  function pad(n: number) { return String(n).padStart(2, '0') }
  function dateStr(d: number) { return `${year}-${pad(month + 1)}-${pad(d)}` }

  const typeColor: Record<string, string> = {
    meeting: '#ffc600', deadline: '#cc3300', review: '#ff8c00', call: '#e8a000', holiday: '#555',
  }

  function prevMonth() { if (month === 0) { setMonth(11); setYear(y => y - 1) } else setMonth(m => m - 1) }
  function nextMonth() { if (month === 11) { setMonth(0); setYear(y => y + 1) } else setMonth(m => m + 1) }

  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`

  return (
    <div className="p-6">
      <SectionHeader title="Content Calendar" sub={loading ? 'Loading…' : `${daysInMon} days — ${events.length} events scheduled`} />

      {/* Month nav */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-2 rounded-lg transition-all"
          style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.15)', color: '#ffc600' }}>
          <ChevronLeft size={16} />
        </button>
        <h2 className="text-base font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
          {MONTHS[month]} {year}
        </h2>
        <button onClick={nextMonth} className="p-2 rounded-lg transition-all"
          style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.15)', color: '#ffc600' }}>
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Grid */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,198,0,0.12)' }}>
        {/* Day headers */}
        <div className="grid grid-cols-7" style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.1)' }}>
          {DAYS.map(d => (
            <div key={d} className="py-2 text-center text-xs font-semibold" style={{ color: '#555' }}>{d}</div>
          ))}
        </div>
        {/* Weeks */}
        {Array.from({ length: cells.length / 7 }, (_, wi) => (
          <div key={wi} className="grid grid-cols-7" style={{ borderBottom: wi < cells.length / 7 - 1 ? '1px solid rgba(255,198,0,0.06)' : undefined }}>
            {cells.slice(wi * 7, wi * 7 + 7).map((day, di) => {
              const ds  = day ? dateStr(day) : ''
              const evs = day ? events.filter(e => e.date === ds) : []
              const isToday = ds === todayStr
              return (
                <div key={di} className="min-h-16 p-1.5" style={{
                  background: '#0f0f0f',
                  borderRight: di < 6 ? '1px solid rgba(255,198,0,0.06)' : undefined,
                }}>
                  {day && (
                    <>
                      <span className="text-xs font-medium flex items-center justify-center w-6 h-6 rounded-full mb-1"
                        style={{
                          background: isToday ? '#ffc600' : 'transparent',
                          color: isToday ? '#000' : '#a0a0a0',
                          fontWeight: isToday ? 700 : 400,
                        }}>
                        {day}
                      </span>
                      <div className="space-y-0.5">
                        {evs.slice(0, 2).map(ev => (
                          <div key={ev.id} className="rounded px-1 py-0.5 truncate text-xs leading-tight"
                            style={{ background: `${typeColor[ev.type] ?? '#555'}18`, color: typeColor[ev.type] ?? '#555', fontSize: '10px' }}>
                            {ev.time ? `${ev.time} ` : ''}{ev.title}
                          </div>
                        ))}
                        {evs.length > 2 && (
                          <div className="text-xs" style={{ color: '#555', fontSize: '10px' }}>+{evs.length - 2} more</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-4">
        {Object.entries(typeColor).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: color }} />
            <span className="text-xs capitalize" style={{ color: '#555' }}>{type}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Scheduling ────────────────────────────────────────────────
export function SchedulingView() {
  const today  = new Date()
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getCalendarEvents().then(setEvents).catch(console.error).finally(() => setLoading(false))
  }, [])

  // Show next 14 days
  const days = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(today)
    d.setDate(today.getDate() + i)
    return d
  })

  function pad(n: number) { return String(n).padStart(2, '0') }
  function toStr(d: Date)  { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }

  const typeColor: Record<string, string> = {
    meeting: '#ffc600', deadline: '#cc3300', review: '#ff8c00', call: '#e8a000', holiday: '#555',
  }
  const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
  const MON_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

  return (
    <div className="p-6">
      <SectionHeader title="Scheduling" sub={loading ? 'Loading…' : '14-day event schedule'} />
      {loading ? <LoadingState /> : (
        <div className="space-y-2">
          {days.map(d => {
            const ds  = toStr(d)
            const evs = events.filter(e => e.date === ds)
            const isToday = ds === toStr(today)
            return (
              <div key={ds} className="rounded-xl overflow-hidden" style={{
                border: `1px solid ${isToday ? 'rgba(255,198,0,0.4)' : 'rgba(255,198,0,0.08)'}`,
                background: '#0f0f0f',
              }}>
                <div className="flex items-start gap-4 p-3">
                  {/* Date column */}
                  <div className="flex-shrink-0 text-center w-12">
                    <p className="text-xs" style={{ color: '#555' }}>{DAY_NAMES[d.getDay()]}</p>
                    <p className="text-xl font-bold leading-tight" style={{
                      fontFamily: 'Syne, sans-serif',
                      color: isToday ? '#ffc600' : '#fff',
                    }}>{d.getDate()}</p>
                    <p className="text-xs" style={{ color: '#555' }}>{MON_NAMES[d.getMonth()]}</p>
                  </div>
                  {/* Events */}
                  <div className="flex-1 min-w-0">
                    {evs.length === 0 ? (
                      <p className="text-xs py-2" style={{ color: '#333' }}>No events</p>
                    ) : (
                      <div className="space-y-1.5">
                        {evs.map(ev => (
                          <div key={ev.id} className="flex items-center gap-3 px-3 py-2 rounded-lg"
                            style={{ background: '#1a1a1a' }}>
                            <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ background: typeColor[ev.type] ?? '#555' }} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white truncate">{ev.title}</p>
                              <div className="flex items-center gap-3 mt-0.5">
                                {ev.time && <span className="text-xs" style={{ color: '#555' }}>{ev.time}</span>}
                                {ev.clientName && <span className="text-xs" style={{ color: '#a0a0a0' }}>{ev.clientName}</span>}
                                <span className="text-xs capitalize px-1.5 py-0.5 rounded"
                                  style={{ background: `${typeColor[ev.type] ?? '#555'}18`, color: typeColor[ev.type] ?? '#555', fontSize: '10px' }}>
                                  {ev.type}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Founder Tasks Board ───────────────────────────────────────
export function FounderTasksView() {
  const { currentUser } = useAuth()
  const { tasks }       = useRealtime()
  const [team, setTeam] = useState<import('../../types/auth.types').User[]>([])

  useEffect(() => {
    if (currentUser?.id) {
      getEmployeesForFounder(currentUser.id).then(setTeam).catch(console.error)
    }
  }, [currentUser?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const teamNames = new Set(team.map(t => t.name))
  const myTasks   = tasks.filter(t => teamNames.has(t.assignee))

  const [filter, setFilter] = useState<'all' | 'todo' | 'in-progress' | 'review' | 'done' | 'overdue'>('all')

  const filtered = myTasks.filter(t => {
    if (filter === 'overdue') return t.status !== 'done' && new Date(t.dueDate) < new Date()
    if (filter === 'all') return true
    return t.status === filter
  })

  const counts = {
    all:           myTasks.length,
    todo:          myTasks.filter(t => t.status === 'todo').length,
    'in-progress': myTasks.filter(t => t.status === 'in-progress').length,
    review:        myTasks.filter(t => t.status === 'review').length,
    done:          myTasks.filter(t => t.status === 'done').length,
    overdue:       myTasks.filter(t => t.status !== 'done' && new Date(t.dueDate) < new Date()).length,
  }

  const priorityColor = (p: string) => ({ urgent: '#cc3300', high: '#ff8c00', medium: '#ffc600', low: '#555' }[p] ?? '#555')
  const statusColor   = (s: string) => ({ 'todo': '#555', 'in-progress': '#ffc600', 'review': '#ff8c00', 'done': '#e8a000' }[s] ?? '#555')
  const statusLabel   = (s: string) => ({ 'todo': 'To Do', 'in-progress': 'In Progress', 'review': 'In Review', 'done': 'Done' }[s] ?? s)

  const FILTERS: { id: typeof filter; label: string; color: string }[] = [
    { id: 'all',         label: 'All',        color: '#a0a0a0' },
    { id: 'todo',        label: 'To Do',       color: '#555' },
    { id: 'in-progress', label: 'In Progress', color: '#ffc600' },
    { id: 'review',      label: 'In Review',   color: '#ff8c00' },
    { id: 'done',        label: 'Done',        color: '#e8a000' },
    { id: 'overdue',     label: 'Overdue',     color: '#cc3300' },
  ]

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Team Tasks</h1>
        <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>
          {myTasks.length} task{myTasks.length !== 1 ? 's' : ''} across {team.length} team member{team.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={{
              background: filter === f.id ? `${f.color}18` : '#0f0f0f',
              color: filter === f.id ? f.color : '#555',
              border: `1px solid ${filter === f.id ? f.color : 'rgba(255,198,0,0.1)'}`,
            }}>
            {f.label} {counts[f.id] > 0 && <span className="ml-1 opacity-70">{counts[f.id]}</span>}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 rounded-xl" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.08)' }}>
          <CheckSquare size={24} className="mx-auto mb-3" style={{ color: '#333' }} />
          <p className="text-sm" style={{ color: '#333' }}>
            {myTasks.length === 0 ? 'No tasks assigned yet. Use the Team page to assign tasks.' : 'No tasks match this filter.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(t => {
            const isOverdue = t.status !== 'done' && new Date(t.dueDate) < new Date()
            return (
              <div key={t.id} className="flex items-center gap-4 px-4 py-3 rounded-xl"
                style={{ background: '#0f0f0f', border: `1px solid ${isOverdue ? 'rgba(204,51,0,0.2)' : 'rgba(255,198,0,0.08)'}` }}>
                {/* Status dot */}
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: statusColor(t.status) }} />
                {/* Task info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{t.title}</p>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="text-xs" style={{ color: '#555' }}>→ {t.assignee}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded"
                      style={{ background: `${statusColor(t.status)}18`, color: statusColor(t.status), fontSize: '10px' }}>
                      {statusLabel(t.status)}
                    </span>
                    <span className="flex items-center gap-1 text-xs" style={{ color: priorityColor(t.priority) }}>
                      <Flag size={9} /> {t.priority}
                    </span>
                  </div>
                </div>
                {/* Due date */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {isOverdue && <AlertTriangle size={12} style={{ color: '#cc3300' }} />}
                  <span className="text-xs" style={{ color: isOverdue ? '#cc3300' : '#555' }}>
                    {new Date(t.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
